import { useMemo, useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import type { FitThesis } from '@ispot/shared'
import { FitThesisStageIndicator } from './FitThesisStageIndicator'
import { FitThesisPersonaAvatar } from './FitThesisPersonaAvatar'
import { FitThesisStatusPill } from './FitThesisStatusPill'
import { attentionRank, coachLineFor, stageProgressFor } from './fitThesisStages'

export interface PersonaSummary {
  sessionId: string
  name: string
  segment: string | null
  photo?: string
  /** Each JTBD carries its own pains so the block panel can resolve painId → label. */
  jtbds: Array<{ id: string; label: string; pains?: Array<{ id: string; label: string; severity?: string }> }>
}

type Filter = 'all' | 'attention' | 'validated' | 'not_started'

interface RowData {
  jtbdId: string
  jtbdLabel: string
  thesis: FitThesis | undefined
}

interface Props {
  personas: PersonaSummary[]
  theses: FitThesis[]
  isLoading: boolean
  onOpenThesis(thesisId: string): void
  /** Called with the exact (persona, jtbd) context the user clicked, skipping the picker dialog. */
  onStart(personaSessionId: string, jtbdId: string): void
}

// ─── Row ───────────────────────────────────────────────────────────────────
function ThesisRow({
  row,
  personaName,
  onOpen,
  onStart,
}: {
  row: RowData
  personaName: string
  onOpen(): void
  onStart(): void
}) {
  const { thesis, jtbdLabel } = row
  const progress = stageProgressFor(thesis)
  const coach = coachLineFor(thesis ?? null, personaName)
  const isEmpty = !thesis

  return (
    <button
      onClick={isEmpty ? onStart : onOpen}
      className="grid w-full grid-cols-[1fr_220px_140px] items-center gap-7 border-t border-border px-3 py-4 text-left transition hover:bg-bg-secondary max-md:grid-cols-1 max-md:items-start max-md:gap-3"
    >
      <div className="min-w-0">
        <div className={`text-body ${isEmpty ? 'font-normal text-fg-secondary' : 'font-medium text-fg'}`}>
          {jtbdLabel}
        </div>
        {coach && (
          <div className="mt-1.5 flex items-start gap-1.5 text-caption text-fg-secondary">
            <span className="mt-0.5 inline-grid h-3.5 w-3.5 flex-shrink-0 place-items-center rounded-full bg-accent-50 text-[9px] font-bold text-accent">
              S
            </span>
            <span className="italic leading-snug">{coach}</span>
          </div>
        )}
      </div>
      <FitThesisStageIndicator progress={progress} />
      <div className="flex justify-end">
        {thesis ? (
          <FitThesisStatusPill thesis={thesis} />
        ) : (
          <span className="inline-flex items-center gap-1 text-caption font-semibold text-accent">
            <Plus className="h-3.5 w-3.5" /> Start thesis
          </span>
        )}
      </div>
    </button>
  )
}

// ─── Persona group ─────────────────────────────────────────────────────────
function PersonaGroup({
  persona,
  rows,
  onOpen,
  onStart,
}: {
  persona: PersonaSummary
  rows: RowData[]
  onOpen(id: string): void
  onStart(personaId: string, jtbdId: string): void
}) {
  if (rows.length === 0) return null
  const attention = rows.filter(r => r.thesis && (r.thesis.status === 'gap' || r.thesis.status === 'draft')).length
  const validated = rows.filter(r => r.thesis?.status === 'validated' || r.thesis?.status === 'complete').length
  const rollupParts = [
    `${rows.length} ${rows.length === 1 ? 'thesis' : 'theses'}`,
    attention > 0 && `${attention} need${attention === 1 ? 's' : ''} attention`,
    validated > 0 && `${validated} validated`,
  ].filter(Boolean) as string[]

  return (
    <section className="flex flex-col">
      <header className="flex items-center gap-3 px-3 pb-3 pt-7">
        <FitThesisPersonaAvatar name={persona.name} photo={persona.photo} size={32} />
        <span className="text-subhead font-semibold text-fg">{persona.name}</span>
        {persona.segment && (
          <span className="border-l border-border pl-3 text-caption text-fg-muted">{persona.segment}</span>
        )}
        <span className="ml-auto font-mono text-caption text-fg-muted">{rollupParts.join(' · ')}</span>
      </header>
      {rows.map(row => (
        <ThesisRow
          key={row.jtbdId}
          row={row}
          personaName={persona.name}
          onOpen={() => row.thesis && onOpen(row.thesis.id)}
          onStart={() => onStart(persona.sessionId, row.jtbdId)}
        />
      ))}
    </section>
  )
}

// ─── Filter chips ──────────────────────────────────────────────────────────
const FILTER_CHIPS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'validated', label: 'Validated' },
  { key: 'not_started', label: 'Not started' },
]

function FilterChips({
  counts,
  value,
  onChange,
}: {
  counts: Record<Filter, number>
  value: Filter
  onChange(f: Filter): void
}) {
  return (
    <div className="flex items-center gap-2 border-y border-border py-2">
      {FILTER_CHIPS.map(chip => {
        const active = value === chip.key
        return (
          <button
            key={chip.key}
            onClick={() => onChange(chip.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-caption transition ${
              active
                ? 'border border-border-strong bg-bg-card text-fg'
                : 'border border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {chip.label}
            <span className="font-mono text-[10px] text-fg-muted">{counts[chip.key]}</span>
          </button>
        )
      })}
      <span className="ml-auto font-mono text-[11px] text-fg-muted">sorted by attention</span>
    </div>
  )
}

function passesFilter(thesis: FitThesis | undefined, filter: Filter): boolean {
  switch (filter) {
    case 'attention':   return !!thesis && (thesis.status === 'gap' || thesis.status === 'draft')
    case 'validated':   return !!thesis && (thesis.status === 'validated' || thesis.status === 'complete')
    case 'not_started': return !thesis
    case 'all':
    default:            return true
  }
}

// ─── Matrix ────────────────────────────────────────────────────────────────
export function FitThesisMatrix({ personas, theses, isLoading, onOpenThesis, onStart }: Props) {
  const [filter, setFilter] = useState<Filter>('all')

  const byKey = useMemo(() => {
    const m = new Map<string, FitThesis>()
    for (const t of theses) m.set(`${t.personaSessionId}::${t.jtbdId}`, t)
    return m
  }, [theses])

  const allRows = useMemo<Array<{ persona: PersonaSummary; row: RowData }>>(() => {
    const out: Array<{ persona: PersonaSummary; row: RowData }> = []
    for (const persona of personas) {
      for (const j of persona.jtbds) {
        const thesis = byKey.get(`${persona.sessionId}::${j.id}`)
        out.push({ persona, row: { jtbdId: j.id, jtbdLabel: j.label, thesis } })
      }
    }
    return out
  }, [personas, byKey])

  const counts: Record<Filter, number> = useMemo(
    () => ({
      all: allRows.length,
      attention: allRows.filter(r => passesFilter(r.row.thesis, 'attention')).length,
      validated: allRows.filter(r => passesFilter(r.row.thesis, 'validated')).length,
      not_started: allRows.filter(r => passesFilter(r.row.thesis, 'not_started')).length,
    }),
    [allRows],
  )

  const grouped = useMemo(() => {
    const groups = new Map<string, RowData[]>()
    for (const { persona, row } of allRows) {
      if (!passesFilter(row.thesis, filter)) continue
      const arr = groups.get(persona.sessionId) ?? []
      arr.push(row)
      groups.set(persona.sessionId, arr)
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => attentionRank(a.thesis) - attentionRank(b.thesis))
    }
    return groups
  }, [allRows, filter])

  const hasAnyJtbd = personas.some(p => p.jtbds.length > 0)
  const visibleCount = Array.from(grouped.values()).reduce((sum, arr) => sum + arr.length, 0)

  if (isLoading && personas.length === 0) {
    return (
      <div className="flex flex-col" aria-busy="true" aria-label="Loading fit theses">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="grid grid-cols-[1fr_220px_140px] items-center gap-7 border-t border-border px-3 py-4"
          >
            <div className="flex flex-col gap-1.5">
              <div className="h-3 w-3/5 animate-pulse rounded bg-bg-secondary" />
              <div className="h-2.5 w-2/5 animate-pulse rounded bg-bg-secondary" />
            </div>
            <div className="h-2 w-full animate-pulse rounded bg-bg-secondary" />
            <div className="h-5 w-20 animate-pulse justify-self-end rounded-full bg-bg-secondary" />
          </div>
        ))}
      </div>
    )
  }

  if (!hasAnyJtbd) {
    return (
      <div className="px-8 py-12 text-center">
        <Sparkles className="mx-auto mb-3 h-8 w-8 text-fg-muted" />
        <p className="text-body text-fg-secondary">
          Your personas don't have JTBDs yet. Add at least one JTBD in the persona workshop, then start a thesis.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <FilterChips counts={counts} value={filter} onChange={setFilter} />
      {personas.map(p => (
        <PersonaGroup
          key={p.sessionId}
          persona={p}
          rows={grouped.get(p.sessionId) ?? []}
          onOpen={onOpenThesis}
          onStart={onStart}
        />
      ))}
      {visibleCount === 0 && (
        <div className="px-8 py-12 text-center text-caption text-fg-muted">No theses match this filter.</div>
      )}
    </div>
  )
}
